import React, { useState } from 'react';

import {
  useFloating,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  offset,
  flip,
  shift,
  arrow,
  FloatingArrow,
  FloatingPortal,
  type Placement,
} from '@floating-ui/react';

import { Z } from '@shared/constants/zIndex';


interface TooltipProps {
  content: string;
  placement?: Placement;
  /** Class for the reference wrapper; defaults to inline sizing. Pass e.g. 'flex w-full' for full-width rows. */
  className?: string;
  children: React.ReactNode;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  placement = 'top',
  className = 'inline-flex items-center',
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // State, not a ref: the arrow middleware is built during render, and handing it a
  // ref is a render-time ref read that makes the React Compiler skip this component.
  const [arrowEl, setArrowEl] = useState<SVGSVGElement | null>(null);

  // Destructured: the compiler reads `refs.setFloating` under a conditional as a ref access.
  const {
    refs: { setReference, setFloating },
    floatingStyles,
    context,
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    middleware: [offset(8), flip(), shift({ padding: 8 }), arrow({ element: arrowEl })],
  });

  const hover = useHover(context, { delay: { open: 200 } });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  return (
    <>
      <span ref={setReference} {...getReferenceProps()} className={className}>
        {children}
      </span>
      {isOpen && (
        <FloatingPortal>
          <div
            ref={setFloating}
            style={{ ...floatingStyles, zIndex: Z.TOAST }}
            {...getFloatingProps()}
            className="bg-foreground text-background rounded-md px-2.5 py-1.5 text-xs font-medium shadow-lg"
          >
            {content}
            <FloatingArrow ref={setArrowEl} context={context} className="fill-foreground" />
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
